"use client"

import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Mail, User, Phone, ArrowRight, Loader2, Lock, ShieldCheck, Eye, EyeOff } from "lucide-react"
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth"
import { doc, setDoc, serverTimestamp } from "firebase/firestore"
import { auth, db } from "@/lib/firebase"
import { toast } from "sonner"

const SignupForm = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState("")

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (formData.password !== formData.confirmPassword) {
      setError("Passkeys do not match.")
      return
    }

    setLoading(true)
    setError("")
    try {
      // 1. Create User in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password)
      const user = userCredential.user

      // 2. Update Auth Profile with Name
      await updateProfile(user, {
         displayName: formData.name
      })

      // 3. Save Student Record to Firestore Database
      await setDoc(doc(db, "users", user.uid), {
         uid: user.uid,
         name: formData.name,
         email: formData.email,
         phone: formData.phone || "",
         role: "student", // default role
         enrolledCourses: [],
         savedCourses: [],
         createdAt: serverTimestamp()
      })

      toast.success("Academic Profile successfully instantiated!")
      navigate("/login") // Direct them to authenticate normally through the portal
    } catch (err: any) {
      console.error(err)
      if (err.code === "auth/email-already-in-use") {
         setError("This email is already registered. Please sign in instead.")
      } else if (err.code === "auth/weak-password") {
         setError("Password must be at least 6 characters for institutional security.")
      } else {
         setError("Registration failed. Please verify your details and try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-white border border-slate-200 p-8 sm:p-10 shadow-2xl">
      <div className="flex flex-col items-center text-center mb-10 space-y-4">
        <div className="w-28 h-28 flex items-center justify-center overflow-hidden">
           <img src="/logo-final.png" alt="Rohith's Academy" className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="text-[10px] font-bold text-[#b48c36] uppercase tracking-[0.4em] mb-2">Student Gateway</p>
          <h1 className="text-3xl font-serif font-medium text-slate-950 leading-tight">
            Create Account
          </h1>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 text-[10px] font-bold uppercase tracking-widest text-center">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div className="space-y-2">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Full Name</label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full bg-slate-50 border border-slate-200 py-4 pl-12 pr-4 text-sm focus:outline-none focus:border-[#b48c36] transition-colors placeholder:text-slate-300"
              placeholder="e.g. Rohith Kumar"
            />
          </div>
        </div>

        {/* Email Address */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
           <div className="space-y-2">
             <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Email Address</label>
             <div className="relative">
               <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
               <input
                 type="email"
                 required
                 value={formData.email}
                 onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                 className="w-full bg-slate-50 border border-slate-200 py-4 pl-12 pr-4 text-sm focus:outline-none focus:border-[#b48c36] transition-colors placeholder:text-slate-300"
                 placeholder="you@example.com"
               />
             </div>
           </div>

           <div className="space-y-2">
             <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Phone Number</label>
             <div className="relative">
               <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
               <input
                 type="tel"
                 required
                 value={formData.phone}
                 onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                 className="w-full bg-slate-50 border border-slate-200 py-4 pl-12 pr-4 text-sm focus:outline-none focus:border-[#b48c36] transition-colors placeholder:text-slate-300"
                 placeholder="+91 00000 00000"
               />
             </div>
           </div>
        </div>

        {/* Password Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type={showPassword ? "text" : "password"}
                required
                minLength={6}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 py-4 pl-12 pr-12 text-sm focus:outline-none focus:border-[#b48c36] transition-colors placeholder:text-slate-300"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#b48c36] transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Confirm Password</label>
            <div className="relative">
              <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type={showConfirmPassword ? "text" : "password"}
                required
                minLength={6}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 py-4 pl-12 pr-12 text-sm focus:outline-none focus:border-[#b48c36] transition-colors placeholder:text-slate-300"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#b48c36] transition-colors"
               >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-slate-950 text-white p-4 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#b48c36] transition-colors flex items-center justify-center disabled:opacity-50 mt-4"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign Up"}
        </button>
      </form>

      <div className="mt-10 pt-8 border-t border-slate-100 text-center flex flex-col items-center">
        <p className="text-xs text-slate-500 font-sans mb-3">Already have an account?</p>
        <Link to="/login" className="text-[10px] font-bold text-slate-950 uppercase tracking-widest hover:text-[#b48c36] transition-colors inline-flex items-center gap-1 group border-b border-transparent hover:border-[#b48c36] pb-1 w-max">
           Sign In Here <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  )
}

export default SignupForm

